import type { Lang } from '../i18n'

export type Reason = 'insomnia' | 'tapering' | 'forced' | 'curious'

export type SymptomKey =
  | 'anxiety'
  | 'racingHeart'
  | 'sweating'
  | 'tremor'
  | 'nausea'
  | 'headache'
  | 'lightSensitivity'
  | 'soundSensitivity'
  | 'muscleTension'
  | 'tinnitus'
  | 'derealization'
  | 'dread'
  | 'seizure'
  | 'hallucination'
  | 'confusion'
  | 'fever'

/** One row of the tablet. `date` is the night's *evening* date, so a 3am entry
 *  belongs to the night before — otherwise every bad night splits across two rows. */
export interface NightEntry {
  date: string // YYYY-MM-DD of the evening
  sleptHours?: number
  quality?: 1 | 2 | 3 | 4 | 5
  wokeInNight?: boolean
  symptoms: SymptomKey[]
  note?: string
  rescueSessions: number // times 3AM mode was opened that night
  ritualsDone: string[] // 'breath' | 'mantra' | 'drift' | 'sound'
  updatedAt: number
}

export interface DoseEntry {
  id: string
  date: string // YYYY-MM-DD
  medication: string
  amountMg: number
  time?: string // HH:MM
}

export interface Settings {
  lang: Lang
  reason: Reason
  ritualTime: string // HH:MM
  voiceGuidance: boolean
  haptics: boolean
  redShift: boolean
  soundVolume: number // 0..1
  aiEndpoint: string
  customMantra: string
  acknowledgedTaperWarning: boolean
  onboarded: boolean
}

export interface State {
  settings: Settings
  nights: Record<string, NightEntry>
  doses: DoseEntry[]
  installedAt: number
}

const KEY = 'nabusu.state.v1'

const defaultSettings: Settings = {
  lang: 'en',
  reason: 'insomnia',
  ritualTime: '22:00',
  voiceGuidance: true,
  haptics: true,
  redShift: true,
  soundVolume: 0.5,
  aiEndpoint: '',
  customMantra: '',
  acknowledgedTaperWarning: false,
  onboarded: false,
}

function emptyState(): State {
  return { settings: { ...defaultSettings }, nights: {}, doses: [], installedAt: Date.now() }
}

let state: State = load()

function load(): State {
  try {
    const raw = localStorage.getItem(KEY)
    if (!raw) return emptyState()
    const parsed = JSON.parse(raw) as Partial<State>
    return {
      settings: { ...defaultSettings, ...(parsed.settings ?? {}) },
      nights: parsed.nights ?? {},
      doses: parsed.doses ?? [],
      installedAt: parsed.installedAt ?? Date.now(),
    }
  } catch {
    // A corrupt store must never be the reason someone can't open this at 3am.
    return emptyState()
  }
}

let saveTimer: number | undefined
function persist(): void {
  clearTimeout(saveTimer)
  saveTimer = window.setTimeout(() => {
    try {
      localStorage.setItem(KEY, JSON.stringify(state))
    } catch {
      /* storage full or blocked — the app still works, it just forgets */
    }
  }, 120)
}

type Listener = () => void
const listeners = new Set<Listener>()

export function subscribe(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit(): void {
  listeners.forEach((fn) => fn())
}

export function getState(): Readonly<State> {
  return state
}

export function getSettings(): Readonly<Settings> {
  return state.settings
}

export function updateSettings(patch: Partial<Settings>): void {
  state.settings = { ...state.settings, ...patch }
  if (patch.lang) localStorage.setItem('nabusu.lang', patch.lang)
  persist()
  emit()
}

/**
 * The date key for "tonight". Anything before 5am belongs to the previous evening —
 * waking at 03:40 is part of last night, not a new day.
 */
export function nightKey(d: Date = new Date()): string {
  const shifted = new Date(d)
  if (shifted.getHours() < 5) shifted.setDate(shifted.getDate() - 1)
  return toISODate(shifted)
}

export function toISODate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function getNight(key = nightKey()): NightEntry {
  return (
    state.nights[key] ?? {
      date: key,
      symptoms: [],
      rescueSessions: 0,
      ritualsDone: [],
      updatedAt: Date.now(),
    }
  )
}

export function updateNight(patch: Partial<NightEntry>, key = nightKey()): void {
  const prev = getNight(key)
  state.nights[key] = { ...prev, ...patch, date: key, updatedAt: Date.now() }
  persist()
  emit()
}

export function markRitualDone(ritual: string): void {
  const night = getNight()
  if (night.ritualsDone.includes(ritual)) return
  updateNight({ ritualsDone: [...night.ritualsDone, ritual] })
}

export function recordRescueSession(): void {
  updateNight({ rescueSessions: getNight().rescueSessions + 1 })
}

export function addDose(dose: Omit<DoseEntry, 'id'>): void {
  state.doses.push({ ...dose, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` })
  persist()
  emit()
}

export function removeDose(id: string): void {
  state.doses = state.doses.filter((d) => d.id !== id)
  persist()
  emit()
}

/** Nights, newest first. */
export function nightsSorted(): NightEntry[] {
  return Object.values(state.nights).sort((a, b) => b.date.localeCompare(a.date))
}

export function nightCount(): number {
  return Object.keys(state.nights).length
}

export type Trend = 'better' | 'worse' | 'flat' | 'unknown'

/** Compare mean sleep of the last 7 recorded nights against the 7 before them. */
export function sleepTrend(): Trend {
  const withSleep = nightsSorted().filter((n) => typeof n.sleptHours === 'number')
  if (withSleep.length < 6) return 'unknown'
  const recent = withSleep.slice(0, 7)
  const older = withSleep.slice(7, 14)
  if (older.length < 3) return 'unknown'
  const mean = (xs: NightEntry[]) => xs.reduce((s, n) => s + (n.sleptHours ?? 0), 0) / xs.length
  const delta = mean(recent) - mean(older)
  if (delta > 0.5) return 'better'
  if (delta < -0.5) return 'worse'
  return 'flat'
}

export function exportAll(): string {
  return JSON.stringify(state, null, 2)
}

export function wipe(): void {
  state = emptyState()
  try {
    localStorage.removeItem(KEY)
  } catch {
    /* ignore */
  }
  emit()
}
